import { useState, useEffect } from "react";
import { invoke } from "../lib/bridge";
import * as Y from "yjs";
import { toUint8Array } from "js-base64";
import { DocMeta } from "../App";
import { DecryptedAvatar } from "./DecryptedAvatar";
import { DocListTab } from "./DocListTab";

interface NotesTabProps {
  docType?: string;
  title?: string;
  onOpenNote: (doc: DocMeta) => void;
  syncVersion?: number;
}

export function NotesTab({ docType = "note", title = "Notes", onOpenNote, syncVersion }: NotesTabProps) {
  const [thumbs, setThumbs] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;

    const loadThumbs = async () => {
      try {
        const docs = await invoke<DocMeta[]>("list_documents", { docType });
        if (cancelled) return;

        const newThumbs: Record<string, string> = {};
        for (const doc of docs) {
          try {
            const updates = await invoke<string[]>("load_updates", { docId: doc.docId });
            if (cancelled) return;
            const yDoc = new Y.Doc();
            for (const b64 of updates) {
              Y.applyUpdate(yDoc, toUint8Array(b64), "load");
            }
            const thumbUrl = yDoc.getText("thumb_url").toString().trim();
            if (thumbUrl) newThumbs[doc.docId] = thumbUrl;
            yDoc.destroy();
          } catch {
            // no thumb for this doc
          }
        }
        if (!cancelled) setThumbs(newThumbs);
      } catch (e) {
        console.error("Failed to load note thumbs:", e);
      }
    };

    loadThumbs();
    return () => { cancelled = true; };
  }, [syncVersion, docType]);

  return (
    <DocListTab
      docType={docType}
      title={title}
      onOpen={onOpenNote}
      syncVersion={syncVersion}
      renderItemExtra={(doc) =>
        thumbs[doc.docId] ? (
          <DecryptedAvatar token={thumbs[doc.docId]} name={doc.title} size="tiny" />
        ) : null
      }
    />
  );
}
